/**
 * Shaping for the Initial ↔ Optimized comparison on the Workflow optimization page —
 * the workflow twin of Config Tuning's "Configuration evaluation" block.
 *
 * Both sides are built from the graph as currently open plus the run's own `changes`:
 * the optimized side writes each change's `after`, the initial side writes its `before`.
 * That way the left column is the run's own baseline even where the graph has since
 * moved, and no second megabyte-sized proposal fetch is needed.
 *
 * Changed-ness is decided by comparing the two rendered values, never by rebuilding a
 * path string — a guessed path shape would quietly mark the wrong field.
 */

import type { WorkflowState } from '../types/workflow';
import type { WorkflowTuningChange } from '../types/workflowTuning';
import { parseChangePath } from './workflowTuningSpec';

export type CompareRoleKind = 'state' | 'coordinator' | 'front' | 'worker' | 'validator';

export interface CompareRole {
  /** Stable select value: `state`, `coordinator`, `front`, `worker:0`, `validator`. */
  key: string;
  kind: CompareRoleKind;
  label: string;
  index?: number;
}

export interface CompareField {
  key: string;
  label: string;
  /** Long prose gets its own block; short values sit on one line. */
  multiline: boolean;
  before: unknown;
  after: unknown;
  changed: boolean;
  /** The proposal introduced this field, or removed it. */
  added: boolean;
  removed: boolean;
}

export function isBlank(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
}

/** Structural equality good enough for snapshot values: strings, numbers, arrays, plain objects. */
export function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (isBlank(a) && isBlank(b)) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function agentsOf(state: WorkflowState | undefined): Record<string, unknown> {
  return record(state?.agents);
}

function workersOf(state: WorkflowState | undefined): unknown[] {
  const workers = agentsOf(state).workers;
  return Array.isArray(workers) ? workers : [];
}

function nameOf(agent: unknown, fallback: string): string {
  const name = record(agent).name;
  return typeof name === 'string' && name.trim() ? name : fallback;
}

/**
 * Roles offered by either side, so an agent the proposal added still appears in the
 * picker instead of being invisible until applied.
 */
export function rolesForState(initial: WorkflowState | undefined, optimized: WorkflowState | undefined): CompareRole[] {
  const roles: CompareRole[] = [{ key: 'state', kind: 'state', label: 'State prompts' }];

  const initialAgents = agentsOf(initial);
  const optimizedAgents = agentsOf(optimized);

  if (initialAgents.coordinator || optimizedAgents.coordinator) {
    roles.push({
      key: 'coordinator',
      kind: 'coordinator',
      label: nameOf(optimizedAgents.coordinator ?? initialAgents.coordinator, 'Coordinator'),
    });
  }
  if (initialAgents.front || optimizedAgents.front) {
    roles.push({
      key: 'front',
      kind: 'front',
      label: nameOf(optimizedAgents.front ?? initialAgents.front, 'Front agent'),
    });
  }

  const workerCount = Math.max(workersOf(initial).length, workersOf(optimized).length);
  for (let index = 0; index < workerCount; index += 1) {
    const agent = workersOf(optimized)[index] ?? workersOf(initial)[index];
    roles.push({ key: `worker:${index}`, kind: 'worker', label: nameOf(agent, `Worker ${index + 1}`), index });
  }

  if (initialAgents.validator || optimizedAgents.validator) {
    roles.push({ key: 'validator', kind: 'validator', label: nameOf(optimizedAgents.validator ?? initialAgents.validator, 'Validator') });
  }
  return roles;
}

export function parseRoleKey(key: string): { kind: CompareRoleKind; index?: number } {
  if (key.startsWith('worker:')) {
    const index = Number(key.slice('worker:'.length));
    return { kind: 'worker', index: Number.isInteger(index) && index >= 0 ? index : 0 };
  }
  if (key === 'coordinator' || key === 'front' || key === 'validator' || key === 'state') return { kind: key };
  return { kind: 'state' };
}

function pickRole(state: WorkflowState | undefined, key: string): unknown {
  const { kind, index } = parseRoleKey(key);
  if (kind === 'state') return state;
  if (kind === 'worker') return workersOf(state)[index ?? 0];
  return agentsOf(state)[kind];
}

const AGENT_KEYS: Array<{ key: string; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'model', label: 'Model' },
  { key: 'temperature', label: 'Temperature' },
  { key: 'mode', label: 'Mode' },
  { key: 'prompt', label: 'Prompt' },
  { key: 'reminder_prompt', label: 'Reminder prompt' },
  { key: 'description', label: 'Description' },
  { key: 'rules', label: 'Rules' },
  { key: 'max_retries', label: 'Max retries' },
];

const STATE_KEYS: Array<{ key: string; label: string }> = [
  { key: 'state_name', label: 'State name' },
  { key: 'primary_goal', label: 'Primary goal' },
  { key: 'secondary_goals', label: 'Secondary goals' },
];

/** Never worth a row: recomputed by the backend, or edited through their own screens. */
const STATE_SKIP = new Set(['state_id', 'agents', 'exit_triggers', 'prompts']);

function humanize(key: string): string {
  const spaced = key.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function isMultiline(before: unknown, after: unknown): boolean {
  return [before, after].some(
    (value) => typeof value === 'string' && (value.length > 80 || value.includes('\n'))
  );
}

function field(key: string, label: string, before: unknown, after: unknown): CompareField {
  return {
    key,
    label,
    multiline: isMultiline(before, after),
    before,
    after,
    changed: !sameValue(before, after),
    added: isBlank(before) && !isBlank(after),
    removed: !isBlank(before) && isBlank(after),
  };
}

/**
 * Rows for one role. Known keys come first in a fixed order; anything else is shown only
 * when it actually differs, so a changed field can never be hidden by the whitelist.
 */
export function compareFields(
  roleKey: string,
  initial: WorkflowState | undefined,
  optimized: WorkflowState | undefined
): CompareField[] {
  const { kind } = parseRoleKey(roleKey);
  const before = pickRole(initial, roleKey);
  const after = pickRole(optimized, roleKey);
  const beforeRecord = record(before);
  const afterRecord = record(after);
  const rows: CompareField[] = [];
  const used = new Set<string>();

  const known = kind === 'state' ? STATE_KEYS : AGENT_KEYS;
  known.forEach(({ key, label }) => {
    if (!(key in beforeRecord) && !(key in afterRecord)) return;
    used.add(key);
    rows.push(field(key, label, beforeRecord[key], afterRecord[key]));
  });

  if (kind === 'state') {
    const beforePrompts = record(beforeRecord.prompts);
    const afterPrompts = record(afterRecord.prompts);
    Array.from(new Set([...Object.keys(beforePrompts), ...Object.keys(afterPrompts)])).forEach((key) => {
      rows.push(field(`prompts.${key}`, humanize(key), beforePrompts[key], afterPrompts[key]));
    });
  }

  Array.from(new Set([...Object.keys(beforeRecord), ...Object.keys(afterRecord)])).forEach((key) => {
    if (used.has(key)) return;
    if (kind === 'state' && STATE_SKIP.has(key)) return;
    if (sameValue(beforeRecord[key], afterRecord[key])) return;
    rows.push(field(key, humanize(key), beforeRecord[key], afterRecord[key]));
  });

  return rows;
}

/** How many changed paths each state carries — drives the picker's badges and its default. */
export function changesByState(changes: WorkflowTuningChange[]): Map<string, number> {
  const counts = new Map<string, number>();
  changes.forEach((change) => {
    const parsed = parseChangePath(change.path);
    if (parsed.kind !== 'state') return;
    counts.set(parsed.ident, (counts.get(parsed.ident) ?? 0) + 1);
  });
  return counts;
}

/** The state a reviewer wants first: the one the run touched most. */
export function busiestState(stateIds: string[], counts: Map<string, number>): string | null {
  if (stateIds.length === 0) return null;
  let best = stateIds[0];
  let bestCount = counts.get(best) ?? 0;
  stateIds.forEach((id) => {
    const count = counts.get(id) ?? 0;
    if (count > bestCount) {
      best = id;
      bestCount = count;
    }
  });
  return best;
}

/** Within a state, open on the role that actually changed rather than on an untouched one. */
export function busiestRole(
  roles: CompareRole[],
  initial: WorkflowState | undefined,
  optimized: WorkflowState | undefined
): string {
  let best = roles[0]?.key ?? 'state';
  let bestCount = -1;
  roles.forEach((role) => {
    const count = compareFields(role.key, initial, optimized).filter((row) => row.changed).length;
    if (count > bestCount) {
      best = role.key;
      bestCount = count;
    }
  });
  return best;
}
